'use client';

import cx from 'classnames';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { useTrackEventAmplitude } from '../../../../../core/Tracking/Amplitude/Amplitude';
import { IconChevronLeft } from '../../../../../design-system/components/icons/ChevronLeft';
import { IconChevronRight } from '../../../../../design-system/components/icons/ChevronRight';
import { useModal } from '../../../../../design-system/components/modal/useModal';
import { Typography } from '../../../../../design-system/components/typography/Typography';
import { TrustpilotMicroTrustScoreWidget } from '../../../../../shared/components/Trustpilot/MicroTrustScoreWidget';
import { useUrlGenerator } from '../../../../../shared/hooks/useUrlGenerator';

import style from './ReassuranceBanner.module.css';
import { ReassuranceBannerPaymentModal } from './ReassuranceBannerPaymentModal';

type Props = {
    className?: string;
};

const ITEM_DISPLAY_DURATION = 5000;

const ITEMS = [TrustPilotItem, PaymentItem, OffersItem];

export default function ReassuranceBanner({ className }: Props) {
    const { formatMessage } = useIntl();
    const { trackEvent } = useTrackEventAmplitude();

    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [activeItem, setActiveItem] = useState(0);

    const playBanner = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
            setActiveItem((previous) => {
                return (previous + 1) % ITEMS.length;
            });
            playBanner();
        }, ITEM_DISPLAY_DURATION);
    }, []);

    const pauseBanner = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = null;
    }, []);

    const goToPrevious = useCallback(() => {
        setActiveItem((previous) => {
            return previous === 0 ? ITEMS.length - 1 : previous - 1;
        });

        playBanner();

        trackEvent((ampli, defaultProperties) => {
            ampli.carouselClicked({
                ...defaultProperties,
                block: 'reassurance',
                direction: 'previous',
            });
        });
    }, [playBanner, trackEvent]);

    const goToNext = useCallback(() => {
        setActiveItem((previous) => {
            return (previous + 1) % ITEMS.length;
        });

        playBanner();

        trackEvent((ampli, defaultProperties) => {
            ampli.carouselClicked({
                ...defaultProperties,
                block: 'reassurance',
                direction: 'next',
            });
        });
    }, [playBanner, trackEvent]);

    useEffect(() => {
        playBanner();

        return () => {
            pauseBanner();
        };
    }, [playBanner, pauseBanner]);

    return (
        <Typography as="div" scale="primary-xxs-regular" className={cx(style.banner, className)}>
            <button
                type="button"
                className={style.navButton}
                onClick={goToPrevious}
                aria-label={formatMessage({ id: 'global.component.carousel.nav.previous' })}
            >
                <IconChevronLeft className={style.navIcon} />
            </button>

            <div className={style.items}>
                {ITEMS.map((Item, index) => {
                    return (
                        <div
                            key={index + 1}
                            className={cx(style.item, {
                                [style.hiddenItem]: index !== activeItem,
                            })}
                            onMouseEnter={() => {
                                pauseBanner();
                            }}
                            onMouseLeave={() => {
                                playBanner();
                            }}
                        >
                            <Item />
                        </div>
                    );
                })}
            </div>

            <button
                type="button"
                className={style.navButton}
                onClick={goToNext}
                aria-label={formatMessage({ id: 'global.component.carousel.nav.next' })}
            >
                <IconChevronRight className={style.navIcon} />
            </button>
        </Typography>
    );
}

function TrustPilotItem() {
    const { trackEvent } = useTrackEventAmplitude();

    return (
        <div
            className={style.trustpilotItem}
            onClick={() => {
                trackEvent((ampli, defaultProperties) => {
                    ampli.trustpilotClicked({
                        ...defaultProperties,
                        block: 'reassurance',
                    });
                });
            }}
        >
            <TrustpilotMicroTrustScoreWidget />
        </div>
    );
}

function PaymentItem() {
    const { formatMessage } = useIntl();
    const { trackEvent } = useTrackEventAmplitude();
    const modal = useModal();

    return (
        <>
            <Typography
                as="button"
                scale="primary-xxs-regular"
                className={style.link}
                onClick={() => {
                    modal.open();

                    trackEvent((ampli, defaultProperties) => {
                        ampli.paymentReassuranceClicked({
                            ...defaultProperties,
                            block: 'reassurance',
                        });
                    });
                }}
            >
                {formatMessage({ id: 'page.home.reassuranceBanner.payment' })}
            </Typography>

            {modal.isOpen ? (
                <ReassuranceBannerPaymentModal
                    {...modal}
                    onClose={() => {
                        trackEvent((ampli, defaultProperties) => {
                            ampli.paymentReassuranceClosed({
                                ...defaultProperties,
                                block: 'reassurance',
                            });
                        });
                    }}
                />
            ) : null}
        </>
    );
}

function OffersItem() {
    const { generatePath } = useUrlGenerator();
    const { formatMessage } = useIntl();
    const { trackEvent } = useTrackEventAmplitude();

    return (
        <div>
            <a
                className={style.link}
                href={generatePath('destination_list')}
                onClick={() => {
                    trackEvent((ampli, defaultProperties) => {
                        ampli.allDestinationsClicked({
                            ...defaultProperties,
                            block: 'reassurance',
                        });
                    });
                }}
            >
                {formatMessage({
                    id: 'page.home.reassuranceBanner.destinationsNumber',
                })}
            </a>{' '}
            · {formatMessage({ id: 'page.home.reassuranceBanner.agenciesNumber' })}
        </div>
    );
}
